import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Copy, Percent, Play, Users } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  deletePresentation,
  fetchPresentations,
} from "../redux/features/presentations/presentationsSlice";
import { selectHasPermission } from "../redux/features/auth/authSlice";
import { toast } from "react-toastify";
import EditPresentationButton from "../components/Presentations/EditPresentationButton";
import AnimatePresentationButton from "../components/Presentations/AnimatePresentationButton";
import ViewPresentationButton from "../components/Presentations/ViewPresentationButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faTimes, faTrash } from "@fortawesome/free-solid-svg-icons";
import ReportPresentationButton from "../components/Presentations/ReportPresentationButton";
import AssignPresentationButton from "../components/Presentations/AssignPresentationButton";

function Presentations() {
  const dispatch = useDispatch();
  const { list, getPresentationLoading, getPresentationError } = useSelector(
    (state) => state.presentation,
  );
  const canDeletePresentations = useSelector((state) =>
    selectHasPermission(state, "delete presentations"),
  );
  const canUpdatePresentations = useSelector((state) =>
    selectHasPermission(state, "update presentations"),
  );
  const canReorderSlides = useSelector((state) =>
    selectHasPermission(state, "reorder slides"),
  );
  const canViewPresentations = useSelector((state) =>
    selectHasPermission(state, "view presentations"),
  );

  const canViewPresentationReport = useSelector((state) =>
    selectHasPermission(state, "view report"),
  );

  const canAssignPresentations = useSelector((state) =>
    selectHasPermission(state, "assign presentations"),
  );

  const canEdit = canUpdatePresentations || canReorderSlides;

  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePresnetationId, setDeleteDeletePresentationId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const handleDelete = async (presentationId) => {
    try {
      await dispatch(deletePresentation(presentationId)).unwrap();
      toast.success("Presentation deleted successfully");
      setShowDeleteModal(false);
      refetchPresentations();
    } catch (error) {
      toast.error("Failed to delete presentation");
    }
  };

  const refetchPresentations = async () => {
    if (initialFetchDone) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    await dispatch(fetchPresentations()).unwrap();
  };

  useEffect(() => {
    refetchPresentations()
      .then(() => {
        setInitialFetchDone(true);
      })
      .catch((err) => console.error("Initial fetch failed:", err));
  }, []);

  const filteredPresentations = list.filter((presentation) => {
    const search = searchTerm.toLowerCase();
    return presentation.title?.toLowerCase().includes(search);
  });

  const features = [
    {
      icon: <Play className="w-4 h-4 text-white" />,
      title: "Total Presentations",
      number: list.length || 0,
    },
    {
      icon: <Users className="w-4 h-4 text-white" />,
      title: "Total Participants",
      number: "25",
    },
    {
      icon: <Percent className="w-4 h-4 text-white" />,
      title: "Avg. Engagement",
      number: "60%",
    },
  ];

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success("Link copied!"),
      (err) => toast.error("Could not copy text.", err),
    );
  };

  return (
    <div className="min-h-screen bg-lightPurple/5">
      <Navbar />

      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10 max-w-7xl mx-auto">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white shadow-md rounded-2xl p-6 flex justify-between items-center hover:shadow-lg transition-all duration-300"
            >
              <div>
                <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                  {feature.title}
                </h2>
                <h2 className="text-2xl font-bold text-gray-800 mt-2">
                  {feature.number}
                </h2>
              </div>
              <div className="bg-purple-400 p-3 rounded-xl text-white shadow-inner">
                {feature.icon}
              </div>
            </div>
          ))}
        </div>

        <div
          className="p-10 my-20 bg-white max-w-7xl mx-auto rounded-2xl shadow-md h-[45rem] overflow-y-auto"
          style={{
            scrollbarWidth: "thin",
          }}
        >
          <h2 className="text-center text-3xl font-semibold text-gray-800 mb-10">
            📑 List of Presentations
          </h2>

          <div className="flex justify-end mb-12">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-72 border border-gray-300 rounded-lg p-2 pl-4 shadow-sm focus:ring-2 focus:ring-purpleColor focus:outline-none transition"
            />
          </div>

          {getPresentationLoading ? (
            <p className="text-center text-gray-500 text-2xl font-medium">
              <FontAwesomeIcon
                icon={faSpinner}
                className={`text-lightPurple mr-1 text-3xl ${getPresentationLoading ? "animate-spin" : ""}`}
              />
              Loading Presentations...
            </p>
          ) : getPresentationError ? (
            <p className="text-center text-red-500 text-2xl font-medium">
              Failed to load presentations.
            </p>
          ) : filteredPresentations.length === 0 ? (
            <p className="text-center text-gray-500 text-2xl font-medium">
              No presentations found.
            </p>
          ) : (
            <div className="space-y-5">
              {filteredPresentations.map((presentation) => {
                const uuid = presentation.public_url
                  ? presentation.public_url.replace(/^\//, "")
                  : presentation.id;

                return (
                  <div
                    key={presentation.id}
                    className="bg-slate-50 border border-gray-100 shadow-sm rounded-2xl p-6 flex justify-between items-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                  >
                    <h2 className="font-semibold text-lg text-gray-800">
                      {presentation.title}
                    </h2>

                    <div className="flex gap-3">
                      <button
                        onClick={() =>
                          copyToClipboard(`${window.location.origin}/${uuid}`)
                        }
                        title="Copy link"
                        className="px-4 py-2 bg-gray-100 text-sm text-gray-700 rounded-lg shadow hover:bg-gray-200 hover:scale-105 transition flex gap-2 items-center"
                      >
                        <Copy className="w-4 h-4" />
                        <span>URL</span>
                      </button>
                      {/** uneeded for now */}
                      {/* <button
                          onClick={() => {
                            window.open(`/${uuid}`, "_blank");
                          }}
                          title="Start New Session"
                          className="px-4 py-2 bg-green-500 text-sm text-white rounded-lg shadow hover:bg-green-600 hover:scale-105 transition flex gap-2 items-center"
                        >
                          <Play className="w-4 h-4" />
                          <span>Start New Session</span>
                        </button> */}

                      {canAssignPresentations && (
                        <AssignPresentationButton presentationId={uuid} />
                      )}

                      {canViewPresentations && (
                        <ViewPresentationButton presentationId={uuid} />
                      )}

                      {canEdit && (
                        <div className="flex gap-2">
                          <EditPresentationButton presentationId={uuid} />
                          {/* <AnimatePresentationButton presentationId={uuid} /> */}
                        </div>
                      )}

                      {canDeletePresentations && (
                        <button
                          onClick={() => {
                            setDeleteDeletePresentationId(presentation.id);
                            setShowDeleteModal(true);
                          }}
                          className="p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm flex gap-1.5 items-center"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                          <span>Delete</span>
                        </button>
                      )}
                      {canViewPresentationReport && (
                        <ReportPresentationButton presentationId={uuid} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60  p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl p-6 relative">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  Confirm Deletion
                </h2>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="text-gray-500 absolute top-2 right-3"
                  aria-label="Close modal"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              {/* Body Text */}
              <p className="text-gray-500 font-medium text-xl my-6 p-2">
                Are you sure you want to delete this presentation ?
              </p>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-8">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 hover:bg-slate-200 hover:text-black border border-slate-200 text-slate-400 text-sm font-medium rounded-lg "
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deletePresnetationId)}
                  className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium text-sm flex items-center"
                >
                  {/* Replaced FontAwesomeIcon with Trash2 icon */}
                  <FontAwesomeIcon icon={faTrash} className="mr-1" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default Presentations;
